import { useState, useEffect } from 'react';

export const useCustomColumns = (columns) => {
  // Dynamically generate availableColumns from columns prop
  const availableColumns = columns
    .filter(
      (col) => col.key !== 'check_id' && col.key !== 'actions' // Exclude fixed columns from settings
    )
    .map((col) => ({
      id: col.key,
      label: typeof col.title === 'string' ? col.title : col.title?.props?.children || col.key,
      hidden: false,
    }));

  // Merge function to ensure all columns are present
  const mergeColumnsOrder = (saved, available) => {
    // Add missing columns from available to saved
    const savedIds = saved.map((col) => col.id);
    const newColumns = available.filter((col) => !savedIds.includes(col.id));
    // Optionally, remove columns that no longer exist in available
    const filteredSaved = saved.filter((col) => available.find((a) => a.id === col.id));
    return [...filteredSaved, ...newColumns];
  };

  const [columnsOrder, setColumnOrder] = useState(availableColumns);

  // On mount, load columnsOrder from localStorage if available
  useEffect(() => {
    const storedOrder = loadColumnsOrderFromLocalStorage();
    if (storedOrder) {
      setColumnOrder(mergeColumnsOrder(storedOrder, availableColumns));
    } else {
      setColumnOrder(availableColumns);
    }
  }, [columns.length]);

  // Load columnsOrder from localStorage
  const loadColumnsOrderFromLocalStorage = () => {
    try {
      const stored = localStorage.getItem('leadsTableColumnsOrder');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.log('loadColumnsOrderFromLocalStorage', e);
    }
    return null;
  };

  // Save columnsOrder to localStorage
  const saveColumnsOrderToLocalStorage = () => {
    try {
      localStorage.setItem('leadsTableColumnsOrder', JSON.stringify(columnsOrder));
    } catch (e) {
      console.log('saveColumnsOrderToLocalStorage', e);
    }
  };

  // Move column up or down in the order
  const moveColumn = (index, direction) => {
    setColumnOrder((prevOrder) => {
      const newOrder = [...prevOrder];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= newOrder.length) return newOrder;
      // Swap
      [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
      return newOrder;
    });
  };

  // Toggle column visibility
  const toggleColumnVisibility = (index) => {
    setColumnOrder((prevOrder) => {
      const newOrder = [...prevOrder];
      newOrder[index] = { ...newOrder[index], hidden: !newOrder[index].hidden };
      return newOrder;
    });
  };

  const columnsRender = [
    columns.find((col) => col.key === 'check_id'),
    ...columnsOrder
      .filter((key) => !key.hidden)
      .map((key) => columns.find((col) => col.key === key.id))
      .filter(Boolean),
    columns.find((col) => col.key === 'actions'),
  ].filter(Boolean);

  return { columnsOrder, columnsRender, toggleColumnVisibility, moveColumn, saveColumnsOrderToLocalStorage };
};
